import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { adminRequest } from "services/adminApi";

const editableKeys = ["enabled", "dailyLimit", "monthlyLimit", "cacheTtlDays"];

function editableState(payload) {
  return {
    enabled: payload?.enabled ?? true,
    dailyLimit: payload?.dailyLimit ?? 10,
    monthlyLimit: payload?.monthlyLimit ?? 100000,
    cacheTtlDays: payload?.cacheTtlDays ?? 30,
  };
}

function TranslationSettingsPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(editableState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminRequest("/admin/translation")
      .then((payload) => {
        setData(payload);
        setForm(editableState(payload));
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const dirty = useMemo(
    () => data && editableKeys.some((key) => form[key] !== data[key]),
    [data, form]
  );
  const usagePercent = data?.monthlyLimit
    ? Math.min(100, (Number(data.monthlyUsage || 0) / Number(data.monthlyLimit)) * 100)
    : 0;

  const updateNumber = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value === "" ? "" : Number(value) }));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = await adminRequest("/admin/translation", {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      setData(payload);
      setForm(editableState(payload));
      setMessage("تم حفظ إعدادات المترجم وتفعيلها فورًا.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} dir="rtl">
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              إدارة مترجم السفر
            </Typography>
            <Typography color="text.secondary">
              تحكم في الخدمة وحدود الاستخدام والتخزين المؤقت دون إصدار نسخة جديدة من التطبيق.
            </Typography>
          </Box>
          {data && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" color="inherit" onClick={load} disabled={loading}>
                تحديث الاستهلاك
              </Button>
              <Chip
                icon={<span className="material-icons">translate</span>}
                color={data.enabled ? "success" : "default"}
                label={data.enabled ? "الخدمة مفعّلة" : "الخدمة متوقفة"}
              />
            </Stack>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        {loading ? (
          <Box textAlign="center" py={8}>
            <CircularProgress color="success" />
          </Box>
        ) : data ? (
          <>
            <Box
              display="grid"
              gap={2}
              mt={3}
              sx={{ gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}
            >
              <Card>
                <CardContent>
                  <Typography variant="button" color="text.secondary">
                    استخدام Luna هذا الشهر
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                    {Number(data.monthlyUsage || 0).toLocaleString("ar")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    من {Number(data.monthlyLimit).toLocaleString("ar")} طلب فعلي
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    color={usagePercent >= 90 ? "error" : "success"}
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="button" color="text.secondary">
                    المتبقي هذا الشهر
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                    {Number(data.monthlyRemaining || 0).toLocaleString("ar")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    الشهر {data.month} بتوقيت UTC
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="button" color="text.secondary">
                    النموذج والحد النصي
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" mt={1}>
                    {data.model}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    حتى {Number(data.maxTextCharacters).toLocaleString("ar")} حرف للطلب
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      color="success"
                      checked={Boolean(form.enabled)}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, enabled: event.target.checked }))
                      }
                    />
                  }
                  label={form.enabled ? "السماح بالترجمة" : "إيقاف جميع طلبات الترجمة"}
                />
                <Alert severity={form.enabled ? "info" : "warning"} sx={{ mt: 2 }}>
                  عند إيقاف الخدمة سيظهر للمستخدم أن المترجم غير متاح مؤقتًا. لن تُحذف الترجمات
                  المخزنة.
                </Alert>
                <Box
                  display="grid"
                  gap={2}
                  mt={3}
                  sx={{ gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}
                >
                  <TextField
                    type="number"
                    label="طلبات المستخدم يوميًا"
                    value={form.dailyLimit}
                    onChange={updateNumber("dailyLimit")}
                    inputProps={{ min: 1, max: 100 }}
                    helperText="من 1 إلى 100، وتشمل نتائج الـCache"
                  />
                  <TextField
                    type="number"
                    label="طلبات Luna شهريًا"
                    value={form.monthlyLimit}
                    onChange={updateNumber("monthlyLimit")}
                    inputProps={{ min: 1, max: 1000000 }}
                    helperText="من 1 إلى 1,000,000؛ لا تشمل نتائج الـCache"
                  />
                  <TextField
                    type="number"
                    label="مدة الـCache بالأيام"
                    value={form.cacheTtlDays}
                    onChange={updateNumber("cacheTtlDays")}
                    inputProps={{ min: 1, max: 365 }}
                    helperText="تُطبق على الترجمات الجديدة من 1 إلى 365 يومًا"
                  />
                </Box>
                <Stack direction="row" spacing={1} mt={3}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={!dirty || saving}
                    onClick={save}
                  >
                    {saving ? "جارٍ الحفظ..." : "حفظ الإعدادات"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={!dirty || saving}
                    onClick={() => setForm(editableState(data))}
                  >
                    إلغاء التعديلات
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Box>
    </DashboardLayout>
  );
}

export default TranslationSettingsPage;
